/* ══════════════════════════════════════════════════════════════════
   Der Archivvermerk am Papier (0265, 05.10.2026, docs/58) — rein, ohne
   Netz, damit er sich ohne Browser prüfen lässt (test/archivseite.test.ts).
   Die Hooks dazu stehen in `daten/archiv.ts`.
   ══════════════════════════════════════════════════════════════════ */

export type Archivumgebung = "entwicklung" | "test" | "produktion";

/**
 * Der Archivvermerk am Papier — „archiviert am … · gesperrt bis …". Die API
 * hängt ihn an Angebotsfassungen (`belege[].archiv`) und Protokolle
 * (`archiv`): die jüngste Fassung im Papier-Register.
 */
export type Archivvermerk = {
  stand: "wartet" | "archiviert" | "fehler" | "geloescht";
  archiviertAm: string | null;
  gesperrtBis: string | null;
  fassung: number;
  umgebung: Archivumgebung | null;
};

export function archivvermerkAus(roh: unknown): Archivvermerk | undefined {
  if (!roh || typeof roh !== "object") return undefined;
  const r = roh as Record<string, unknown>;
  const stand = r["stand"];
  if (stand !== "wartet" && stand !== "archiviert" && stand !== "fehler" && stand !== "geloescht") return undefined;
  const umgebung = r["umgebung"];
  return {
    stand,
    archiviertAm: typeof r["archiviert_am"] === "string" ? r["archiviert_am"] : null,
    gesperrtBis: typeof r["gesperrt_bis"] === "string" ? r["gesperrt_bis"] : null,
    fassung: typeof r["fassung"] === "number" ? r["fassung"] : 1,
    umgebung: umgebung === "entwicklung" || umgebung === "test" || umgebung === "produktion" ? umgebung : null,
  };
}

const tagFormat = new Intl.DateTimeFormat("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric" });

/** Ein ISO-Zeitpunkt als Tag in deutscher Zeit — `05.10.2026`. */
export function archivTag(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : tagFormat.format(d);
}

/**
 * Der Vermerk als Satz — oder `null`, wenn es nichts zu sagen gibt.
 * „archiviert am 05.10.2026 · gesperrt bis 30.01.2035"; in der Testumgebung
 * mit dem Zusatz, dass die Sperre nur einen Tag hält.
 */
export function archivSatz(
  t: (pfad: string, werte?: Record<string, string | number>) => string,
  a: Archivvermerk | undefined,
): string | null {
  if (!a) return null;
  if (a.stand === "archiviert") {
    const satz = t("archiv.vermerk.archiviert", { am: archivTag(a.archiviertAm), bis: archivTag(a.gesperrtBis) });
    return a.umgebung && a.umgebung !== "produktion" ? `${satz} ${t("archiv.vermerk.test")}` : satz;
  }
  return t(`archiv.vermerk.${a.stand}`);
}
